import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { Container } from '@/components/layout';

export interface PageHeaderSkeletonProps {
  /** Show breadcrumbs placeholder */
  showBreadcrumbs?: boolean;
  /** Show description placeholder */
  showDescription?: boolean;
  /** Show action buttons placeholder */
  showActions?: boolean;
  /** Use gradient background variant */
  variant?: 'default' | 'gradient';
  /** Custom container size */
  containerSize?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Additional content height (for children) */
  childrenHeight?: number;
  /** Custom className */
  className?: string;
}

/**
 * Skeleton placeholder for PageHeader component.
 * Reserves the exact layout space to prevent CLS.
 */
export function PageHeaderSkeleton({
  showBreadcrumbs = false,
  showDescription = true,
  showActions = false,
  variant = 'default',
  containerSize = 'xl',
  childrenHeight,
  className,
}: PageHeaderSkeletonProps) {
  const useFullBleed = variant === 'gradient';

  return (
    <header
      className={cn(
        'w-full relative overflow-hidden border-b border-border',
        variant === 'default' && 'bg-background',
        variant === 'gradient' && 'bg-background',
        className
      )}
    >
      {/* Full-bleed background layer for gradient variant */}
      {useFullBleed && (
        <div
          className="absolute inset-y-0 w-[100vw] left-1/2 -translate-x-1/2 bg-gradient-to-r from-background via-background to-muted/30 dark:from-dark-base dark:via-dark-base dark:to-dark-elevated/50"
          aria-hidden="true"
        />
      )}
      <Container size={containerSize} padding className="relative z-10">
        <div className="py-4 lg:py-6">
          {/* Breadcrumbs skeleton */}
          {showBreadcrumbs && (
            <div className="flex items-center gap-2 mb-2">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-3 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
          )}

          {/* Main header content */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            {/* Title and description */}
            <div className="flex-1 min-w-0">
              {/* Title */}
              <Skeleton className="h-8 w-2/3 sm:h-9 lg:h-10 max-w-md" />

              {/* Description */}
              {showDescription && (
                <div className="mt-1.5 space-y-1">
                  <Skeleton className="h-4 w-full max-w-2xl" />
                  <Skeleton className="h-4 w-3/4 max-w-xl" />
                </div>
              )}

              {/* Children placeholder */}
              {childrenHeight && <Skeleton className="mt-3" style={{ height: childrenHeight }} />}
            </div>

            {/* Action buttons skeleton */}
            {showActions && (
              <div className="flex gap-2 sm:flex-shrink-0">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-20" />
              </div>
            )}
          </div>
        </div>
      </Container>
    </header>
  );
}
